<template>
  <div v-if="ready">
    <div v-if="emptyMessage" class="stats-empty" data-testid="empty-state">
      <DisplayState iconName="empty" :stateText="emptyMessage" stateSubText="" />
    </div>
    <div v-else class="stats-grid">
      <template v-if="error">
        <div 
          v-for="n in count"
          :key="n"
          class="stat-card"
        >
          <DisplayState 
            iconName="error"
            stateText="Something went wrong"
            :stateSubText="errorMessage"
          />
        </div>
      </template>
      <template v-else>
        <div 
          v-for="(stat, index) in stats"
          :key="index"
          class="stat-card"
        >
          <div class="stat-value">{{ formatValue(stat.value) }}</div>
          <div class="stat-label">{{ stat.label }}</div>
        </div>
      </template>
    </div>
  </div>
  <StatsSkeleton v-else :count="count" />
</template>


<script setup>
import { ref } from 'vue';
import StatsSkeleton from './StatsSkeleton.vue';
import DisplayState from '@/components/Automation/Table/DisplayState.vue';
import { bindSetGraphsReady } from '@/Modules/Grafer/common/populateGraph';

const props = defineProps({
  count: {
    type: Number,
    default: 4
  }
});

const stats = ref([]);
const ready = ref(false);
const error = ref(false);
const errorMessage = ref('Something went wrong');
const emptyMessage = ref('');

const setReady = bindSetGraphsReady(ready);

function populateComponent(data, options = {}) {
  emptyMessage.value = options.emptyMessage || '';
  if (emptyMessage.value) {
    error.value = false;
    stats.value = [];
    ready.value = true;
    return;
  }

  if (!data || (Array.isArray(data) && data.length === 0)) {
    error.value = true;
    errorMessage.value = 'No data available.';
    stats.value = [];
    ready.value = true;
    return;
  }

  try {
    stats.value = data;
    error.value = false;
    ready.value = true;
  } catch (err) {
    error.value = true;
    errorMessage.value = err?.message || 'Failed to load statistics.';
    stats.value = [];
    ready.value = true;
  }
}

function formatValue(val) {
  if (val == null) return '-';
  return typeof val === 'number' ? val.toLocaleString() : val;
}

defineExpose({ populateComponent, setReady });

</script>


<style scoped>
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  grid-auto-rows: 1fr; 
  gap: 16px;
  height: 100%;
}

.stat-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: var(--color-background);
  border: var(--border-card);
  border-radius: var(--border-radius-card);
  box-shadow: var(--shadow-card);
  padding: 20px;
  text-align: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover {
  box-shadow: var(--shadow-card-hover);
  transform: translateY(-2px);
}

.stat-value {
  font-size: 32px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 14px;
  color: #475569;
}
</style>
